import {BaseStore} from './base.store';
import {RootStore} from './index';
import {action, computed, makeObservable, observable} from 'mobx';
import {DateRange, handleDateRange} from '@src/utils/datesUtils';
import {FilterKey, ReportMetadataAndData} from '@src/dtos/reports.dto';
import reportService from '@src/services/report.service';
import {ReportsDataRequest, WidgetDataRequest} from '@src/dtos/reports-data-request';
import {SeverityType, WidgetName} from '@src/utils/enums';
import {ReportFilterState} from '@src/components/features/reports-and-logs/new-reports/report-viewer/useReportFilters';

export class ReportsStore extends BaseStore {
    public reportsMetadataAndData: Record<string, ReportMetadataAndData> = {};
    public reportsDashboardMetadataAndData: Record<string, ReportMetadataAndData> = {};
    public dateRange: DateRange = 'Last 7 days';

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            reportsMetadataAndData: observable,
            reportsDashboardMetadataAndData: observable,
            getAllReportFinishLoad: computed,
            dateRange: observable,
            setDateRange: action
        });
    }

    public reset() {
        this.reportsMetadataAndData = {};
        this.reportsDashboardMetadataAndData = {};
    }

    public setDateRange(dateRange: string | DateRange) {
        this.dateRange = dateRange;
    }

    public async loadNewReportData(reportName: string, filters?: ReportFilterState) {
        const { customerId, serviceId } = this.rootStore.customerStore;
        const reports = this.rootStore.userPreferencesStore.reportsPreferences;
        if (!reports.some(_ => _.reportName === reportName)) return;
        const bodyRequestReportData: ReportsDataRequest = this.createBodyRequest(reportName, filters);
        return await reportService.getReportsData(customerId, serviceId, bodyRequestReportData);
    }

    public async loadWidgetData(widgetName: WidgetName, direction?: 'inbound' | 'outbound') {
        const { customerId, serviceId } = this.rootStore.customerStore;
        const bodyRequestWidgetData: WidgetDataRequest = this.createWidgetBodyRequest(widgetName, direction);
        return await reportService.getWidgetData(customerId, serviceId, bodyRequestWidgetData);
    }

    get getAllReportFinishLoad() {
        return Object.values(this.reportsMetadataAndData).every(item => !item.metadata.isLoading);
    }

    public createBodyRequest(reportName: string, filters?: ReportFilterState): ReportsDataRequest {
        const { fromDate, toDate } = handleDateRange(this.dateRange);
        const formatFilters = filters ? Object.fromEntries(
            Object.keys(filters).map(key => {
                let value = (filters as any)[key];
                if (key === FilterKey.severity) {
                    if (value === '') return [key, undefined];
                    if (value === SeverityType.INFO) value = 'informational';
                }
                return [key, typeof value === 'string' ? value?.toLowerCase() : value];
            })
        ) : {};

        return {
            reportName,
            startTime: fromDate,
            endTime: toDate,
            extraFilters: formatFilters
        };
    }

    public createWidgetBodyRequest(widgetName: WidgetName, direction?: 'inbound' | 'outbound'): WidgetDataRequest {
        const { fromDate, toDate } = handleDateRange(this.dateRange);
        return {
            widgetName: widgetName,
            startTime: fromDate,
            endTime: toDate,
            extraFilters: {
                direction: direction?.toLowerCase(),
                showUSA: this.rootStore.userPreferencesStore.reportsShowUSA,
            }
        };
    }
}